import { useEffect, useState } from 'react'
import type { Hub, Section } from '@kronos/domain'

/**
 * Routing, in a page and no dependency.
 *
 * There are a few route shapes and there is a hard constraint on their form, so
 * a router library would be carrying a lot of machinery to solve a problem this
 * app does not have — and every kilobyte is downloaded over workshop wifi on a
 * phone. If nested layouts or route-level data loading ever arrive, swap this
 * out; it is deliberately small enough to delete.
 *
 * **The constraint** (see `deploy/static.Caddyfile` and CLAUDE.md): a single
 * five-character path segment at the root is *reserved* for serials — Caddy
 * sends `/K7F38` to the API, which resolves the scan and redirects here. So
 * every client route below carries a prefix or a second segment. A bare
 * `/parts` route would never reach the browser: Caddy would swallow it, and the
 * symptom would look like a routing bug rather than a rule.
 */

export type Route =
  /** Everyone's home — and it looks different for each job. `/`. */
  | { readonly name: 'today' }
  /** A technician's availability and the requests to come in. */
  | { readonly name: 'schedule' }
  /** A technician's own shifts. Under `/my/` because `/hours` would be one segment of five. */
  | { readonly name: 'hours' }
  /** A technician's work: jobs under way and builds to pick up. */
  | { readonly name: 'work' }
  /** Intake: taking a delivery in — optionally of an item already chosen. */
  | { readonly name: 'receive'; readonly item: string | null }
  /** What is on the shelf, by item. */
  | { readonly name: 'inventory' }
  /** One item (a component type) — what it is, how many we have, where they came from. */
  | { readonly name: 'item'; readonly id: string }
  | { readonly name: 'fleet' }
  | { readonly name: 'build'; readonly section: Section | null }
  | { readonly name: 'design' }
  | { readonly name: 'people'; readonly section: Section | null }
  /** Who may SSH into which robot or office computer (admin). */
  | { readonly name: 'connectivity'; readonly section: Section | null }
  | { readonly name: 'component'; readonly id: string }
  | { readonly name: 'robot'; readonly id: string }
  /** A blank label scanned before it was used: the old intake landing (§9.4). */
  | { readonly name: 'intake'; readonly serial: string | null }
  /** A job in progress. Linkable, because "carry on with this one" is a thing
   *  people say to each other across a workshop. */
  | { readonly name: 'execution'; readonly id: string }
  | { readonly name: 'scan'; readonly unknown: string | null }

/**
 * Every place is a URL, so a reload stays put and a link can be sent.
 *
 * The paths avoid the reserved root segment (one segment of exactly five
 * characters is a serial — see the Caddyfile): `/robots`, `/design`,
 * `/receive` and `/inventory` are longer, and the rest carry a second segment.
 */
const SECTION_NAMES: readonly Section[] = ['orders', 'to-buy', 'schedule', 'hours', 'team', 'access', 'machines', 'people']
const asSection = (s: string | undefined): Section | null =>
  SECTION_NAMES.includes(s as Section) ? (s as Section) : null

export function parseRoute(path: string, search: string): Route {
  const params = new URLSearchParams(search)
  const segments = path.split('/').filter(Boolean)
  const [first, second] = segments

  if (first === 'component' && second) return { name: 'component', id: second }
  if (first === 'robot' && second) return { name: 'robot', id: second }
  if (first === 'execution' && second) return { name: 'execution', id: second }
  if (first === 'item' && second) return { name: 'item', id: second }
  if (first === 'intake') return { name: 'intake', serial: params.get('serial') }
  if (first === 'scan') return { name: 'scan', unknown: params.get('unknown') }
  if (first === 'my' && second === 'schedule') return { name: 'schedule' }
  if (first === 'my' && second === 'hours') return { name: 'hours' }
  if (first === 'work') return { name: 'work' }
  if (first === 'receive') return { name: 'receive', item: params.get('item') }
  if (first === 'inventory') return { name: 'inventory' }
  if (first === 'robots') return { name: 'fleet' }
  if (first === 'build') return { name: 'build', section: asSection(second) }
  if (first === 'design') return { name: 'design' }
  if (first === 'people') return { name: 'people', section: asSection(second) }
  if (first === 'connectivity') return { name: 'connectivity', section: asSection(second) }
  return { name: 'today' }
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'today':
      return '/'
    case 'schedule':
      return '/my/schedule'
    case 'hours':
      return '/my/hours'
    case 'work':
      return '/work'
    case 'receive':
      return route.item ? `/receive?item=${encodeURIComponent(route.item)}` : '/receive'
    case 'inventory':
      return '/inventory'
    case 'item':
      return `/item/${route.id}`
    case 'fleet':
      return '/robots'
    // A place with no section chosen still gets a second segment, so the path
    // can never be one segment of five; the screen picks the first section the
    // person holds.
    case 'build':
      return `/build/${route.section ?? 'orders'}`
    case 'design':
      return '/design'
    case 'people':
      return `/people/${route.section ?? 'schedule'}`
    case 'connectivity':
      return `/connectivity/${route.section ?? 'access'}`
    case 'component':
      return `/component/${route.id}`
    case 'robot':
      return `/robot/${route.id}`
    case 'execution':
      return `/execution/${route.id}`
    case 'intake':
      return route.serial ? `/intake?serial=${encodeURIComponent(route.serial)}` : '/intake'
    case 'scan':
      return route.unknown ? `/scan?unknown=${encodeURIComponent(route.unknown)}` : '/scan'
  }
}

/** The place a route belongs to, for highlighting the nav. */
export function hubOf(route: Route): Hub | null {
  switch (route.name) {
    case 'today':
    case 'schedule':
    case 'hours':
    case 'work':
    case 'receive':
    case 'inventory':
    case 'fleet':
    case 'build':
    case 'design':
    case 'people':
    case 'connectivity':
      return route.name
    // An item page is reached from the shelf.
    case 'item':
      return 'inventory'
    default:
      return null
  }
}

export function navigate(route: Route): void {
  window.history.pushState(null, '', hrefFor(route))
  // pushState does not fire popstate, so nothing would re-render without this.
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  )

  useEffect(() => {
    const onPop = (): void => {
      setRoute(parseRoute(window.location.pathname, window.location.search))
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  return route
}
